import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiSearch,
  FiLoader,
  FiServer,
  FiCalendar,
  FiUser,
  FiActivity,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiRefreshCw,
  FiHash,
  FiFileText,
  FiCheckSquare,
  FiCheck,
  FiPlus,
} from 'react-icons/fi';
import dayjs from 'dayjs';
import { message } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';

import {
  useQueryRemoteWorklist,
  EnrichedWorklistItem,
} from '../../../helpers/api/dicom/dicom';
import { useWorklistSources } from '../../../helpers/api/mwl/mwl';

import { FormField } from '../common/FormField';
import { Button } from '../common/Button';
import { Select } from '../common/Select';
import { Table } from '../common/Table';
import { DateInput } from '../common/DateInput';
import PregnancyModal from './PregnancyModal';

interface Props {
  onWorklistItemSelect: (item: EnrichedWorklistItem) => void;
  selectedItem?: EnrichedWorklistItem | null;
  /** Keys of all selected rows (AccessionNumber / StudyUID / composite). */
  selectedKeys?: string[];
  onConfirmWithPregnancy?: (
    pregnancyData: PregnancyFormState & { trimester: Trimester }
  ) => void;
  onDirectSaveFromPregnancy?: (worklistItem: any, pregnancyData: any) => void;
  /** Notifies parent when the DICOM/MWL server selection changes (for import-mwl). */
  onServerChange?: (serverId: number | null) => void;
  /** When true, row click only selects — skip pregnancy modal side-effects (scheduling import). */
  selectionOnly?: boolean;
}

function mwlItemKey(item: EnrichedWorklistItem): string {
  const mwl = item.mwl || {};
  return (
    mwl.AccessionNumber ||
    mwl.StudyInstanceUID ||
    `${mwl.PatientID}-${mwl.RequestedProcedureID || ''}`
  );
}

export interface WorklistFilters {
  patient_name: string;
  patient_id: string;
  patient_birth_date: string;
  patient_sex: string;

  accession_number: string;
  study_instance_uid: string;
  requesting_physician: string;

  scheduled_station_ae_title: string;
  scheduled_procedure_step_start_date: string;
  scheduled_procedure_step_start_time: string;
  scheduled_performing_physician_name: string;
  modality: string;

  server_id: number | null;
}

const initialFilters: Omit<WorklistFilters, 'server_id'> = {
  patient_name: '',
  patient_id: '',
  patient_birth_date: '',
  patient_sex: '',
  accession_number: '',
  study_instance_uid: '',
  requesting_physician: '',
  scheduled_station_ae_title: '',
  scheduled_procedure_step_start_date: dayjs().format('YYYY-MM-DD'),
  scheduled_procedure_step_start_time: '',
  scheduled_performing_physician_name: '',
  modality: '',
};

const modalities = [
  { value: '', label: 'All Modalities' },
  { value: 'US', label: 'US - Ultrasound' },
  { value: 'CT', label: 'CT - Computed Tomography' },
  { value: 'MR', label: 'MR - Magnetic Resonance' },
  { value: 'XA', label: 'XA - X-Ray Angiography' },
  { value: 'CR', label: 'CR - Computed Radiography' },
  { value: 'DX', label: 'DX - Digital Radiography' },
  { value: 'MG', label: 'MG - Mammography' },
  { value: 'NM', label: 'NM - Nuclear Medicine' },
  { value: 'PT', label: 'PT - PET' },
  { value: 'RF', label: 'RF - Radiofluoroscopy' },
  { value: 'OT', label: 'OT - Other' },
];

const patientSexOptions = [
  { value: '', label: 'All' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

type Trimester = 'T1' | 'T2' | 'T3';
type PregnancyStatus =
  | 'Active'
  | 'Delivered'
  | 'Miscarried'
  | 'Terminated'
  | 'Unknown';

interface PregnancyFormState {
  lmp: string;
  edd: string;
  gaWeeks: string;
  gaDays: string;
  crl: string;
  bpd: string;
  hc: string;
  ac: string;
  fl: string;
  status?: PregnancyStatus;
  grossesse_classee_a_risque?: boolean | null;
  risque_anemie?: boolean;
  risque_hta?: boolean;
  risque_diabete?: boolean;
  risque_cardiopathie?: boolean;
  risque_metrorragie?: boolean;
  risque_infection?: boolean;
  risque_preeclampsie?: boolean;
  risque_eclampsie?: boolean;
  risque_autre_details?: string;
}

interface ParsedPatientName {
  family: string;
  given: string;
  middle: string;
  suffix: string;
  prefix: string;
  formatted: string;
  fullName: string;
}

const parsePatientName = (patientName: string): ParsedPatientName => {
  if (!patientName || patientName.trim() === '') {
    return {
      family: '',
      given: '',
      middle: '',
      suffix: '',
      prefix: '',
      formatted: 'Unknown Patient',
      fullName: 'Unknown Patient',
    };
  }

  const parts = patientName.split('^').map((p) => p.trim());

  const family = parts[0] || '';
  const given = parts[1] || '';
  const middle = parts[2] || '';
  const suffix = parts[3] || '';
  const prefix = parts[4] || '';

  let formatted = '';

  // Add prefix if exists (Dr, Ms, etc.)
  if (prefix) formatted += `${prefix} `;

  // Add given name
  if (given && given !== 'UNKNOWN') formatted += `${given} `;

  // Add middle name
  if (middle) formatted += `${middle} `;

  // Add family name
  if (family && family !== 'UNKNOWN') formatted += family;

  // Add suffix if exists (Jr, Sr, etc.)
  if (suffix) formatted += ` ${suffix}`;

  // Clean up
  formatted = formatted.trim();

  // Fallback if empty after parsing
  if (!formatted) {
    formatted = 'Unknown Patient';
  }

  // Build full name for display
  const fullName = formatted;

  return {
    family,
    given,
    middle,
    suffix,
    prefix,
    formatted,
    fullName,
  };
};

const WorklistSearch: React.FC<Props> = ({
  onWorklistItemSelect,
  selectedItem,
  selectedKeys,
  onConfirmWithPregnancy,
  onDirectSaveFromPregnancy,
  onServerChange,
  selectionOnly = false,
}) => {
  const { t } = useTranslation('Common');
  // State
  const [selectedServer, setSelectedServer] = useState<number | null>(null);
  const [searchFilters, setSearchFilters] =
    useState<Omit<WorklistFilters, 'server_id'>>(initialFilters);
  const [worklistResults, setWorklistResults] = useState<
    EnrichedWorklistItem[]
  >([]);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [resolutionStats, setResolutionStats] = useState<{
    patients_found: number;
    pregnancies_found: number;
    studies_found: number;
  } | null>(null);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [localSearchFilters, setLocalSearchFilters] = useState({
    patient_name: '',
    patient_id: '',
    accession_number: '',
    requesting_physician: '',
    physician: '',
    procedure_description: '',
    location: '',
  });

  const [uniqueLocations, setUniqueLocations] = useState<string[]>([]);
  const [uniqueRequestingPhysicians, setUniqueRequestingPhysicians] = useState<
    string[]
  >([]);

  const [pregnancyModalOpen, setPregnancyModalOpen] = useState(false);
  const [selectedWorklistItem, setSelectedWorklistItem] =
    useState<EnrichedWorklistItem | null>(null);
  const [pregnancyModalMode, setPregnancyModalMode] = useState<
    'create' | 'edit-local' | 'edit-backend'
  >('create');
  const [pregnancyInitialData, setPregnancyInitialData] = useState<
    Partial<PregnancyFormState & { trimester: Trimester }> | undefined
  >(undefined);
  const [editingPregnancyId, setEditingPregnancyId] = useState<
    number | undefined
  >(undefined);

  const worklistStudyDateStr = useMemo(() => {
    if (!selectedWorklistItem) return undefined;
    const d =
      selectedWorklistItem.mwl.ScheduledProcedureStepSequence?.[0]
        ?.ScheduledProcedureStepStartDate;
    if (d && String(d).length === 8) {
      return dayjs(String(d), 'YYYYMMDD').format('YYYY-MM-DD');
    }
    return undefined;
  }, [selectedWorklistItem]);

  const worklistUltrasoundDateStr = useMemo(() => {
    const ud = selectedWorklistItem?.resolution?.pregnancy?.ultrasound_date;
    if (!ud) return undefined;
    const parsed = dayjs(ud);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
  }, [selectedWorklistItem]);

  const [localPregnancyData, setLocalPregnancyData] = useState<
    Record<string, PregnancyFormState & { trimester: Trimester }>
  >({});


  const { data: sourcesData, isLoading: isLoadingServers } =
    useWorklistSources('worklist');
  const worklistSources = sourcesData?.destinations ?? [];

  const { mutate: queryWorklist, isPending: isQueryingWorklist } =
    useQueryRemoteWorklist(
      (response) => {
        const data = response?.data;
        if (response?.status === 'success' && data?.results) {
          const results = data.results as EnrichedWorklistItem[];
          // The import picker only shows procedures that have not already
          // produced a local appointment. Other worklist consumers can still
          // see the complete external MWL.
          const visibleResults = selectionOnly
            ? results.filter((item) => !item.resolution?.appointment_exists)
            : results;
          setWorklistResults(visibleResults);
          setResolutionStats(data.resolution_stats || null);

          setLocalSearchFilters({
            patient_name: '',
            patient_id: '',
            accession_number: '',
            requesting_physician: '',
            physician: '',
            procedure_description: '',
            location: '',
          });

          if (visibleResults.length === 0) {
            messageI18n.info('worklist.noItemsFound');
          } else {
            const stats = data.resolution_stats;
            messageI18n.success('worklist.searchSuccess', {
              totalResults: visibleResults.length,
              patientsFound: stats?.patients_found || 0,
              pregnanciesFound: stats?.pregnancies_found || 0,
            });
          }
        } else {
          message.warning(response?.message || tToast('worklist.queryNoResults'));
          setWorklistResults([]);
          setResolutionStats(null);
        }
      },
      (error) => {
        messageI18n.error('worklist.queryFailed');
        console.error(error);
        setWorklistResults([]);
        setResolutionStats(null);
      }
    );

  useEffect(() => {
    if (worklistSources.length && !selectedServer) {
      setSelectedServer(worklistSources[0].id);
    }
  }, [worklistSources, selectedServer]);

  useEffect(() => {
    onServerChange?.(selectedServer);
  }, [selectedServer, onServerChange]);

  useEffect(() => {
    if (selectedServer && worklistResults.length === 0) {
      handleSearch();
    }
  }, [selectedServer]);

  useEffect(() => {
    if (worklistResults.length > 0) {
      const locations = new Set<string>();
      worklistResults.forEach((item) => {
        const location =
          item.mwl.ScheduledProcedureStepSequence?.[0]
            ?.ScheduledProcedureStepLocation;
        if (location && location.trim() !== '') {
          locations.add(location);
        }
      });
      setUniqueLocations(Array.from(locations).sort());

      const physicians = new Set<string>();
      worklistResults.forEach((item) => {
        const physician = item.mwl.RequestingPhysician;
        if (physician && physician.trim() !== '') {
          const parsedName = parsePatientName(physician);
          if (parsedName.fullName !== 'Unknown Patient') {
            physicians.add(parsedName.fullName);
          }
        }
      });
      setUniqueRequestingPhysicians(Array.from(physicians).sort());
    } else {
      setUniqueLocations([]);
      setUniqueRequestingPhysicians([]);
    }
  }, [worklistResults]);

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setSearchFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    if (!selectedServer) {
      messageI18n.warning('worklist.selectDicomServer');
      return;
    }

    const queryParams: any = { aeId: selectedServer };

    if (searchFilters.patient_id) {
      queryParams.patientId = searchFilters.patient_id;
    }
    if (searchFilters.patient_name) {
      queryParams.patientName = `*${searchFilters.patient_name}*`;
    }
    if (searchFilters.patient_birth_date) {
      queryParams.patientBirthDate = dayjs(
        searchFilters.patient_birth_date
      ).format('YYYYMMDD');
    }
    if (searchFilters.patient_sex) {
      queryParams.patientSex = searchFilters.patient_sex;
    }
    if (searchFilters.accession_number) {
      queryParams.accessionNumber = searchFilters.accession_number;
    }
    if (searchFilters.study_instance_uid) {
      queryParams.studyInstanceUID = searchFilters.study_instance_uid;
    }
    if (searchFilters.requesting_physician) {
      queryParams.requestingPhysician = searchFilters.requesting_physician;
    }
    if (searchFilters.scheduled_station_ae_title) {
      queryParams.scheduledStationAeTitle =
        searchFilters.scheduled_station_ae_title;
    }
    if (searchFilters.scheduled_procedure_step_start_date) {
      const day = dayjs(
        searchFilters.scheduled_procedure_step_start_date
      ).format('YYYY-MM-DD');
      queryParams.startDateFrom = day;
      queryParams.startDateTo = day;
    }
    if (searchFilters.scheduled_procedure_step_start_time) {
      queryParams.startTimeFrom =
        searchFilters.scheduled_procedure_step_start_time.replace(/:/g, '') +
        '00';
    }
    if (searchFilters.scheduled_performing_physician_name) {
      queryParams.performingPhysicianName =
        searchFilters.scheduled_performing_physician_name;
    }
    if (searchFilters.modality) {
      queryParams.modality = searchFilters.modality;
    }

    queryWorklist(queryParams);
  };

  const handleReset = () => {
    setSearchFilters(initialFilters);
    setWorklistResults([]);
  };

  const handleItemSelect = (item: EnrichedWorklistItem) => {
    const patientId = item.mwl.PatientID || '';
    const localPregnancy = localPregnancyData[patientId];

    if (!selectionOnly) {
      const hasActivePregnancy =
        item.resolution?.active_pregnancy_exists &&
        item.resolution?.pregnancy?.pregnancy_status === 'Active';

      if (!hasActivePregnancy && !localPregnancy && item.mwl.PatientSex === 'F') {
        setSelectedWorklistItem(item);
        setPregnancyModalMode('create');
        setPregnancyInitialData(undefined);
        setEditingPregnancyId(undefined);
        setPregnancyModalOpen(true);
      }
    }

    if (localPregnancy) {
      const enrichedItem = {
        ...item,
        localPregnancy,
      };
      onWorklistItemSelect(enrichedItem as EnrichedWorklistItem);
    } else {
      onWorklistItemSelect(item);
    }
  };

  const handleAddPregnancy = (item: EnrichedWorklistItem) => {
    setSelectedWorklistItem(item);
    const patientId = item.mwl.PatientID || '';
    const existingLocal = localPregnancyData[patientId];

    if (existingLocal) {
      setPregnancyModalMode('edit-local');
      setPregnancyInitialData(existingLocal);
      setEditingPregnancyId(undefined);
    } else {
      setPregnancyModalMode('create');
      setPregnancyInitialData(undefined);
      setEditingPregnancyId(undefined);
    }
    setPregnancyModalOpen(true);
  };

  const handleEditBackendPregnancy = (item: EnrichedWorklistItem) => {
    setSelectedWorklistItem(item);
    const pregnancy = item.resolution.pregnancy;

    if (pregnancy) {
      const initialData: Partial<
        PregnancyFormState & { trimester: Trimester }
      > = {
        lmp: pregnancy.last_menstrual_period
          ? dayjs(pregnancy.last_menstrual_period).format('YYYY-MM-DD')
          : '',
        edd: pregnancy.estimated_due_date
          ? dayjs(pregnancy.estimated_due_date).format('YYYY-MM-DD')
          : '',
        gaWeeks: pregnancy.gestational_age_weeks?.toString() || '',
        gaDays: pregnancy.gestational_age_days?.toString() || '',
        crl: pregnancy.preg_crl?.toString() || '',
        bpd: pregnancy.preg_bpd?.toString() || '',
        hc: pregnancy.preg_hc?.toString() || '',
        ac: pregnancy.preg_ac?.toString() || '',
        fl: pregnancy.preg_fl?.toString() || '',
        grossesse_classee_a_risque:
          pregnancy.grossesse_classee_a_risque ?? null,
        risque_anemie: pregnancy.risque_anemie === true,
        risque_hta: pregnancy.risque_hta === true,
        risque_diabete: pregnancy.risque_diabete === true,
        risque_cardiopathie: pregnancy.risque_cardiopathie === true,
        risque_metrorragie: pregnancy.risque_metrorragie === true,
        risque_infection: pregnancy.risque_infection === true,
        risque_preeclampsie: pregnancy.risque_preeclampsie === true,
        risque_eclampsie: pregnancy.risque_eclampsie === true,
        risque_autre_details: pregnancy.risque_autre_details || '',
        status: pregnancy.pregnancy_status as any,
      };

      const w = pregnancy.gestational_age_weeks || 0;
      let trimester: Trimester = 'T1';
      if (w >= 28) trimester = 'T3';
      else if (w >= 14) trimester = 'T2';
      initialData.trimester = trimester;

      setPregnancyModalMode('edit-backend');
      setPregnancyInitialData(initialData);
      setEditingPregnancyId(pregnancy.id);
    }
    setPregnancyModalOpen(true);
  };

  const handleSaveLocalPregnancy = (
    pregnancyData: PregnancyFormState & { trimester: Trimester }
  ) => {
    if (!selectedWorklistItem) return;

    const mwl = selectedWorklistItem.mwl;
    const patientId = mwl.PatientID || '';

    setLocalPregnancyData((prev) => ({
      ...prev,
      [patientId]: pregnancyData,
    }));

    messageI18n.success('worklist.pregnancyAdded');
  };

  const handleBackendPregnancyUpdate = (updatedPregnancy: any) => {
    setWorklistResults((prevResults) => {
      const updatedResults = prevResults.map((item) => {
        if (
          item.resolution.pregnancy &&
          item.resolution.pregnancy.id === updatedPregnancy.id
        ) {
          const isStillActive = updatedPregnancy.pregnancy_status === 'Active';

          return {
            ...item,
            resolution: {
              ...item.resolution,
              active_pregnancy_exists: isStillActive,
              pregnancy: updatedPregnancy,
            },
          };
        }
        return item;
      });

      return updatedResults;
    });
  };

  const handleSaveAndConfirm = (
    pregnancyData: PregnancyFormState & { trimester: Trimester }
  ) => {
    if (!selectedWorklistItem) return;

    const mwl = selectedWorklistItem.mwl;
    const patientId = mwl.PatientID || '';

    setLocalPregnancyData((prev) => ({
      ...prev,
      [patientId]: pregnancyData,
    }));

    setPregnancyModalOpen(false);

    if (onDirectSaveFromPregnancy) {
      onDirectSaveFromPregnancy(selectedWorklistItem, pregnancyData);
    } else if (onConfirmWithPregnancy) {
      messageI18n.success('worklist.pregnancySavedConfirming');
      onConfirmWithPregnancy(pregnancyData);
    }
  };

  const formatDicomDate = (dicomDate?: string) => {
    if (!dicomDate) return 'N/A';
    if (dicomDate.length === 8) {
      return dayjs(dicomDate, 'YYYYMMDD').format('DD MMM YYYY');
    }
    return dicomDate;
  };

  const formatDicomTime = (dicomTime?: string) => {
    if (!dicomTime) return '';
    if (dicomTime.length >= 6) {
      const hours = dicomTime.substring(0, 2);
      const minutes = dicomTime.substring(2, 4);
      return `${hours}:${minutes}`;
    }
    return dicomTime;
  };

  const handleLocalSearchChange = (field: string, value: string) => {
    setLocalSearchFilters((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const newFilters: ColumnFiltersState = [];

    if (localSearchFilters.patient_name) {
      newFilters.push({
        id: 'patient_name',
        value: localSearchFilters.patient_name,
      });
    }
    if (localSearchFilters.patient_id) {
      newFilters.push({
        id: 'patient_id',
        value: localSearchFilters.patient_id,
      });
    }
    if (localSearchFilters.accession_number) {
      newFilters.push({
        id: 'accession_number',
        value: localSearchFilters.accession_number,
      });
    }
    if (localSearchFilters.requesting_physician) {
      newFilters.push({
        id: 'requesting_physician',
        value: localSearchFilters.requesting_physician,
      });
    }
    if (localSearchFilters.physician) {
      newFilters.push({ id: 'physician', value: localSearchFilters.physician });
    }
    if (localSearchFilters.procedure_description) {
      newFilters.push({
        id: 'procedure_description',
        value: localSearchFilters.procedure_description,
      });
    }
    if (localSearchFilters.location) {
      newFilters.push({
        id: 'location',
        value: localSearchFilters.location,
      });
    }

    setColumnFilters(newFilters);
  }, [localSearchFilters]);

  const columns = useMemo<ColumnDef<EnrichedWorklistItem>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'mwl.PatientName',
        header: ({ column }) => {
          const isSorted = column.getIsSorted();
          return (
            <div
              className="cursor-pointer flex items-center"
              onClick={column.getToggleSortingHandler()}
            >
              <span>Patient Name</span>
              <span className="ml-1 text-slate-400">
                {isSorted === 'asc' ? '↑' : isSorted === 'desc' ? '↓' : '↕'}
              </span>
            </div>
          );
        },
        cell: ({ row }) => {
          const mwl = row.original.mwl;
          const parsedName = parsePatientName(mwl.PatientName);
          const isSelected =
            selectionOnly &&
            selectedKeys?.includes(mwlItemKey(row.original));
          return (
            <div className="flex items-center gap-2.5 font-medium text-gray-900 dark:text-white">
              {selectionOnly && (
                <span
                  className={
                    isSelected
                      ? 'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-blue-600 bg-blue-600 text-white'
                      : 'h-5 w-5 shrink-0 rounded-md border border-slate-300 bg-white dark:border-gray-600 dark:bg-gray-800'
                  }
                >
                  {isSelected && <FiCheck size={13} strokeWidth={3} />}
                </span>
              )}
              <span>{parsedName.fullName}</span>
            </div>
          );
        },
        filterFn: (row, columnId, filterValue) => {
          const mwl = row.original.mwl;
          const searchStr = filterValue.toLowerCase();
          const parsedName = parsePatientName(mwl.PatientName);
          return parsedName.fullName.toLowerCase().includes(searchStr);
        },
        enableSorting: true,
      },
      {
        id: 'patient_id',
        accessorKey: 'mwl.PatientID',
        header: ({ column }) => {
          const isSorted = column.getIsSorted();
          return (
            <div
              className="cursor-pointer flex items-center"
              onClick={column.getToggleSortingHandler()}
            >
              <span>Patient ID</span>
              <span className="ml-1 text-slate-400">
                {isSorted === 'asc' ? '↑' : isSorted === 'desc' ? '↓' : '↕'}
              </span>
            </div>
          );
        },
        cell: ({ row }) => {
          const mwl = row.original.mwl;
          return (
            <div className="text-sm text-gray-900 dark:text-white">
              {mwl.PatientID || 'N/A'}
            </div>
          );
        },
        filterFn: (row, columnId, filterValue) => {
          const mwl = row.original.mwl;
          const searchStr = filterValue.toLowerCase();
          return (mwl.PatientID || '').toLowerCase().includes(searchStr);
        },
        enableSorting: true,
      },
      {
        id: 'accession_number',
        accessorKey: 'mwl.AccessionNumber',
        header: ({ column }) => {
          const isSorted = column.getIsSorted();
          return (
            <div
              className="cursor-pointer flex items-center"
              onClick={column.getToggleSortingHandler()}
            >
              <span>Accession Number</span>
              <span className="ml-1 text-slate-400">
                {isSorted === 'asc' ? '↑' : isSorted === 'desc' ? '↓' : '↕'}
              </span>
            </div>
          );
        },
        cell: ({ row }) => {
          const mwl = row.original.mwl;
          return (
            <div className="text-sm text-gray-900 dark:text-white">
              {mwl.AccessionNumber || 'N/A'}
            </div>
          );
        },
        filterFn: (row, columnId, filterValue) => {
          const mwl = row.original.mwl;
          const searchStr = filterValue.toLowerCase();
          return (mwl.AccessionNumber || '').toLowerCase().includes(searchStr);
        },
        enableSorting: true,
      },
      {
        id: 'requesting_physician',
        accessorKey: 'mwl.RequestingPhysician',
        header: ({ column }) => {
          const isSorted = column.getIsSorted();
          return (
            <div
              className="cursor-pointer flex items-center"
              onClick={column.getToggleSortingHandler()}
            >
              <span>Requesting Physician</span>
              <span className="ml-1 text-slate-400">
                {isSorted === 'asc' ? '↑' : isSorted === 'desc' ? '↓' : '↕'}
              </span>
            </div>
          );
        },
        cell: ({ row }) => {
          const mwl = row.original.mwl;
          const parsedName = parsePatientName(mwl.RequestingPhysician || '');
          return (
            <div className="text-sm text-gray-900 dark:text-white">
              {parsedName.fullName}
            </div>
          );
        },
        filterFn: (row, columnId, filterValue) => {
          const mwl = row.original.mwl;
          const parsedName = parsePatientName(mwl.RequestingPhysician || '');
          const searchStr = filterValue.toLowerCase();
          return parsedName.fullName.toLowerCase().includes(searchStr);
        },
        enableSorting: true,
      },
      {
        id: 'location',
        accessorFn: (row) =>
          row.mwl.ScheduledProcedureStepSequence?.[0]
            ?.ScheduledProcedureStepLocation,
        header: ({ column }) => {
          const isSorted = column.getIsSorted();
          return (
            <div className="flex items-center">
              <div
                className="cursor-pointer flex items-center"
                onClick={column.getToggleSortingHandler()}
              >
                <span>Location</span>
                <span className="ml-1 text-slate-400">
                  {isSorted === 'asc' ? '↑' : isSorted === 'desc' ? '↓' : '↕'}
                </span>
              </div>
            </div>
          );
        },
        cell: ({ row }) => {
          const scheduledStep =
            row.original.mwl.ScheduledProcedureStepSequence?.[0];
          return (
            <div className="text-sm text-gray-900 dark:text-white">
              {scheduledStep?.ScheduledProcedureStepLocation || 'N/A'}
            </div>
          );
        },
        filterFn: (row, columnId, filterValue) => {
          const scheduledStep =
            row.original.mwl.ScheduledProcedureStepSequence?.[0];
          const location = scheduledStep?.ScheduledProcedureStepLocation || '';
          return location === filterValue;
        },
        enableSorting: true,
      },
      {
        id: 'scheduled_date',
        accessorFn: (row) =>
          row.mwl.ScheduledProcedureStepSequence?.[0]
            ?.ScheduledProcedureStepStartDate,
        header: ({ column }) => {
          const isSorted = column.getIsSorted();
          return (
            <div className="flex items-center">
              <div
                className="cursor-pointer flex items-center"
                onClick={column.getToggleSortingHandler()}
              >
                <span>Scheduled Date</span>
                <span className="ml-1 text-slate-400">
                  {isSorted === 'asc' ? '↑' : isSorted === 'desc' ? '↓' : '↕'}
                </span>
              </div>
            </div>
          );
        },
        cell: ({ row }) => {
          const scheduledStep =
            row.original.mwl.ScheduledProcedureStepSequence?.[0];
          return (
            <div className="text-sm text-gray-900 dark:text-white">
              {scheduledStep
                ? formatDicomDate(scheduledStep.ScheduledProcedureStepStartDate)
                : 'N/A'}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        id: 'scheduled_time',
        accessorFn: (row) =>
          row.mwl.ScheduledProcedureStepSequence?.[0]
            ?.ScheduledProcedureStepStartTime,
        header: 'Scheduled Time',
        cell: ({ row }) => {
          const scheduledStep =
            row.original.mwl.ScheduledProcedureStepSequence?.[0];
          return (
            <div className="text-sm text-gray-900 dark:text-white">
              {scheduledStep
                ? formatDicomTime(
                    scheduledStep.ScheduledProcedureStepStartTime
                  ) || 'N/A'
                : 'N/A'}
            </div>
          );
        },
        enableSorting: false,
      },
      // {
      //   id: 'physician',
      //   accessorFn: (row) =>
      //     row.mwl.ScheduledProcedureStepSequence?.[0]
      //       ?.ScheduledPerformingPhysicianName,
      //   header: ({ column }) => {
      //     const isSorted = column.getIsSorted();
      //     return (
      //       <div
      //         className="cursor-pointer flex items-center"
      //         onClick={column.getToggleSortingHandler()}
      //       >
      //         <span>Physician</span>
      //         <span className="ml-1 text-slate-400">
      //           {isSorted === 'asc' ? '↑' : isSorted === 'desc' ? '↓' : '↕'}
      //         </span>
      //       </div>
      //     );
      //   },
      //   cell: ({ row }) => {
      //     const scheduledStep =
      //       row.original.mwl.ScheduledProcedureStepSequence?.[0];
      //     return (
      //       <div className="text-sm text-gray-900 dark:text-white">
      //         {scheduledStep?.ScheduledPerformingPhysicianName || 'N/A'}
      //       </div>
      //     );
      //   },
      //   filterFn: (row, columnId, filterValue) => {
      //     const scheduledStep =
      //       row.original.mwl.ScheduledProcedureStepSequence?.[0];
      //     const searchStr = filterValue.toLowerCase();
      //     return (scheduledStep?.ScheduledPerformingPhysicianName || '')
      //       .toLowerCase()
      //       .includes(searchStr);
      //   },
      //   enableSorting: true,
      // },
      {
        id: 'procedure_description',
        accessorFn: (row) =>
          row.mwl.ScheduledProcedureStepSequence?.[0]
            ?.ScheduledProcedureStepDescription,
        header: ({ column }) => {
          const isSorted = column.getIsSorted();
          return (
            <div
              className="cursor-pointer flex items-center"
              onClick={column.getToggleSortingHandler()}
            >
              <span>Description</span>
              <span className="ml-1 text-slate-400">
                {isSorted === 'asc' ? '↑' : isSorted === 'desc' ? '↓' : '↕'}
              </span>
            </div>
          );
        },
        cell: ({ row }) => {
          const scheduledStep =
            row.original.mwl.ScheduledProcedureStepSequence?.[0];
          return (
            <div
              className="text-sm text-gray-900 dark:text-white truncate max-w-[200px]"
              title={scheduledStep?.ScheduledProcedureStepDescription || ''}
            >
              {scheduledStep?.ScheduledProcedureStepDescription || 'N/A'}
            </div>
          );
        },
        filterFn: (row, columnId, filterValue) => {
          const scheduledStep =
            row.original.mwl.ScheduledProcedureStepSequence?.[0];
          const searchStr = filterValue.toLowerCase();
          return (scheduledStep?.ScheduledProcedureStepDescription || '')
            .toLowerCase()
            .includes(searchStr);
        },
        enableSorting: true,
      },
      {
        id: 'resolution_status',
        header: 'Status',
        cell: ({ row }) => {
          const resolution = row.original.resolution;
          return (
            <div className="flex flex-col gap-1">
              {resolution.patient_exists ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full w-fit">
                  <FiCheckSquare size={10} /> Patient in System
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full w-fit">
                  <FiUser size={10} /> New Patient
                </span>
              )}
              {resolution.active_pregnancy_exists && resolution.pregnancy && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-400 px-2 py-0.5 rounded-full w-fit">
                  <FiActivity size={10} /> Active Pregnancy
                </span>
              )}
              {resolution.study_exists && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full w-fit">
                  <FiFileText size={10} /> Study Exists
                </span>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'pregnancy_info',
        header: 'Pregnancy Info',
        cell: ({ row }) => {
          const item = row.original;
          const mwl = item.mwl;
          const resolution = item.resolution;
          const patientId = mwl.PatientID || '';
          const localPregnancy = localPregnancyData[patientId];

          const hasPregnancy =
            resolution.pregnancy !== null && resolution.pregnancy !== undefined;
          const isPregnancyActive =
            hasPregnancy && resolution.pregnancy.pregnancy_status === 'Active';

          if (localPregnancy) {
            return (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-pink-600 text-white px-1.5 py-0.5 rounded-full font-bold">
                    {localPregnancy.trimester}
                  </span>
                  <span className="text-[10px] font-semibold text-pink-700 dark:text-pink-400">
                    Local
                  </span>
                </div>
                <div className="text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
                  {localPregnancy.edd && (
                    <div>
                      <span className="font-medium">EDD:</span>{' '}
                      {dayjs(localPregnancy.edd).format('DD MMM YY')}
                    </div>
                  )}
                  {localPregnancy.gaWeeks && (
                    <div>
                      <span className="font-medium">GA:</span>{' '}
                      {localPregnancy.gaWeeks}w {localPregnancy.gaDays || 0}d
                    </div>
                  )}
                </div>
              </div>
            );
          }

          if (hasPregnancy && isPregnancyActive) {
            const pregnancy = resolution.pregnancy;
            return (
              <div className="">
                <div className="text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
                  {pregnancy.estimated_due_date && (
                    <div>
                      <span className="font-medium">EDD:</span>{' '}
                      {dayjs(pregnancy.estimated_due_date).format('DD MMM YY')}
                    </div>
                  )}
                  {pregnancy.gestational_age_weeks !== null && (
                    <div>
                      <span className="font-medium">GA:</span>{' '}
                      {pregnancy.gestational_age_weeks}w{' '}
                      {pregnancy.gestational_age_days || 0}d
                    </div>
                  )}
                </div>
              </div>
            );
          }

          if (hasPregnancy && !isPregnancyActive) {
            const pregnancy = resolution.pregnancy;
            return (
              <div className="text-xs text-gray-600 dark:text-gray-400">
                <span className="text-[10px] font-semibold">
                  Previous: {pregnancy.pregnancy_status}
                </span>
              </div>
            );
          }

          return <span className="text-xs text-slate-400">N/A</span>;
        },
        enableSorting: false,
      },
      {
        id: 'pregnancy_actions',
        header: 'Actions',
        cell: ({ row }) => {
          const item = row.original;
          const mwl = item.mwl;
          const resolution = item.resolution;
          const patientId = mwl.PatientID || '';
          const localPregnancy = localPregnancyData[patientId];

          const hasPregnancy =
            resolution.pregnancy !== null && resolution.pregnancy !== undefined;
          const isPregnancyActive =
            hasPregnancy && resolution.pregnancy.pregnancy_status === 'Active';

          if (localPregnancy) {
            return (
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddPregnancy(item);
                }}
                className="w-full text-xs py-1"
              >
                Edit
              </Button>
            );
          }

          if (hasPregnancy && isPregnancyActive) {
            return (
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditBackendPregnancy(item);
                }}
                className="w-full text-xs py-1"
              >
                Edit
              </Button>
            );
          }

          if (hasPregnancy && !isPregnancyActive) {
            return (
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddPregnancy(item);
                }}
                className="w-full text-xs py-1"
              >
                <FiPlus size={12} className="mr-1" />
                Pregnancy
              </Button>
            );
          }

          return (
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleAddPregnancy(item);
              }}
              className="w-full text-xs py-1"
            >
              <FiPlus size={12} className="mr-1" />
              Pregnancy
            </Button>
          );
        },
        enableSorting: false,
      },
    ],
    [localPregnancyData, selectedKeys, selectionOnly]
  );

  const visibleColumns = useMemo(() => {
    if (!selectionOnly) return columns;
    const importColumnIds = new Set([
      'patient_name',
      'patient_id',
      'accession_number',
      'scheduled_date',
      'scheduled_time',
      'procedure_description',
      'resolution_status',
    ]);
    return columns.filter((column) => importColumnIds.has(String(column.id)));
  }, [columns, selectionOnly]);

  // Create table instance
  const table = useReactTable({
    data: worklistResults,
    columns: visibleColumns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-gray-800">
      <PregnancyModal
        isOpen={pregnancyModalOpen}
        onClose={() => setPregnancyModalOpen(false)}
        worklistItem={selectedWorklistItem}
        onSaveLocal={handleSaveLocalPregnancy}
        onUpdateSuccess={handleBackendPregnancyUpdate}
        onSaveAndConfirm={handleSaveAndConfirm}
        initialData={pregnancyInitialData}
        mode={pregnancyModalMode}
        pregnancyId={editingPregnancyId}
        studyDate={worklistStudyDateStr}
        ultrasoundDate={worklistUltrasoundDateStr}
      />
      {/* Advanced Filters Form */}
      {isFiltersVisible && (
        <div className="max-h-[42vh] overflow-y-auto border-b border-slate-200 bg-slate-50/70 p-5 dark:border-gray-700 dark:bg-gray-900/40">
          <>
            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              Advanced Filters
            </h4>
            {/* Advanced Filters */}
            <div className="mb-1 grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 md:grid-cols-2 xl:grid-cols-4">
              {/* Modality */}
              <FormField label="Modality" icon={FiActivity}>
                <Select
                  id="modality"
                  name="modality"
                  value={searchFilters.modality}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                >
                  {modalities.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Select>
              </FormField>

              {/* Patient Sex */}
              <FormField label="Patient Sex" htmlFor="patient_sex">
                <Select
                  id="patient_sex"
                  name="patient_sex"
                  value={searchFilters.patient_sex}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                >
                  {patientSexOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormField>

              {/* Patient Birth Date */}
              <FormField label="Patient Birth Date" icon={FiCalendar}>
                <input
                  type="date"
                  name="patient_birth_date"
                  value={searchFilters.patient_birth_date}
                  onChange={handleFilterChange}
                  max={dayjs().format('YYYY-MM-DD')}
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormField>

              {/* Scheduled Time */}
              <FormField label="Scheduled Time" icon={FiClock}>
                <input
                  type="time"
                  name="scheduled_procedure_step_start_time"
                  value={searchFilters.scheduled_procedure_step_start_time}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormField>

              {/* Study Instance UID */}
              <FormField
                label="Study Instance UID"
                htmlFor="study_instance_uid"
              >
                <input
                  type="text"
                  id="study_instance_uid"
                  name="study_instance_uid"
                  value={searchFilters.study_instance_uid}
                  onChange={handleFilterChange}
                  placeholder="Enter Study UID"
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormField>

              {/* Requesting Physician */}
              <FormField
                label="Requesting Physician"
                htmlFor="requesting_physician"
              >
                <input
                  type="text"
                  id="requesting_physician"
                  name="requesting_physician"
                  value={searchFilters.requesting_physician}
                  onChange={(e) => {
                    handleFilterChange(e);
                    if (worklistResults.length > 0) {
                      handleLocalSearchChange(
                        'requesting_physician',
                        e.target.value
                      );
                    }
                  }}
                  placeholder="Physician name"
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormField>

              {/* Scheduled Station AE Title */}
              <FormField
                label="Scheduled Station AE"
                htmlFor="scheduled_station_ae_title"
              >
                <input
                  type="text"
                  id="scheduled_station_ae_title"
                  name="scheduled_station_ae_title"
                  value={searchFilters.scheduled_station_ae_title}
                  onChange={handleFilterChange}
                  placeholder="AE Title"
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormField>

              {/* Scheduled Performing Physician */}
              <FormField
                label="Scheduled Physician"
                htmlFor="scheduled_performing_physician_name"
              >
                <input
                  type="text"
                  id="scheduled_performing_physician_name"
                  name="scheduled_performing_physician_name"
                  value={searchFilters.scheduled_performing_physician_name}
                  onChange={(e) => {
                    // Update both backend filter and local filter
                    handleFilterChange(e);
                    if (worklistResults.length > 0) {
                      handleLocalSearchChange('physician', e.target.value);
                    }
                  }}
                  placeholder="Performing physician"
                  className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormField>
            </div>
          </>
        </div>
      )}

      {/* Results Table */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Server Selection and Action Buttons - Always Visible */}
        <div className="border-b border-slate-200 bg-white px-5 py-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_minmax(0,1fr)_auto] lg:items-start">
            {/* Left: Server Selection */}
            <div className="flex min-w-0 flex-col gap-1.5">
              <label className="flex items-center gap-1 text-xs font-semibold text-slate-700 dark:text-slate-300">
                <FiServer size={12} />
                DICOM Server
                <span className="text-red-500">*</span>
              </label>
              <Select
                id="server"
                name="server"
                value={selectedServer || ''}
                onChange={(e) => setSelectedServer(Number(e.target.value))}
                disabled={isLoadingServers}
                className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-900 dark:disabled:text-slate-600"
              >
                {isLoadingServers ? (
                  <option>Loading servers...</option>
                ) : worklistSources.length ? (
                  worklistSources.map((source) => (
                    <option key={source.id} value={source.id}>
                      {source.aeTitle} ({source.hostname}:{source.port})
                    </option>
                  ))
                ) : (
                  <option value="">
                    No worklist servers — register an SCU machine with C-FIND
                  </option>
                )}
              </Select>
            </div>

            {/* Center: Basic Filters */}
            <div className="grid min-w-0 grid-cols-2 items-end gap-3 md:grid-cols-4">
              {/* Patient Name */}
              <div className="flex min-w-0 flex-col gap-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Patient Name
                </label>
                <input
                  type="text"
                  name="patient_name"
                  value={searchFilters.patient_name}
                  onChange={(e) => {
                    handleFilterChange(e);
                    if (worklistResults.length > 0) {
                      handleLocalSearchChange('patient_name', e.target.value);
                    }
                  }}
                  placeholder="Family^Given"
                  className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-gray-900 placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-slate-500"
                />
              </div>

              {/* Patient ID */}
              <div className="flex min-w-0 flex-col gap-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Patient ID
                </label>
                <input
                  type="text"
                  name="patient_id"
                  value={searchFilters.patient_id}
                  onChange={(e) => {
                    handleFilterChange(e);
                    if (worklistResults.length > 0) {
                      handleLocalSearchChange('patient_id', e.target.value);
                    }
                  }}
                  placeholder="ID"
                  className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-gray-900 placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-slate-500"
                />
              </div>

              {/* Scheduled Date */}
              <div className="flex min-w-0 flex-col gap-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Scheduled Date
                </label>
                <DateInput
                  id="scheduled_procedure_step_start_date"
                  name="scheduled_procedure_step_start_date"
                  value={searchFilters.scheduled_procedure_step_start_date}
                  onChange={handleFilterChange}
                  compact
                />
              </div>

              {/* Accession Number */}
              <div className="flex min-w-0 flex-col gap-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Accession #
                </label>
                <input
                  type="text"
                  name="accession_number"
                  value={searchFilters.accession_number}
                  onChange={(e) => {
                    handleFilterChange(e);
                    if (worklistResults.length > 0) {
                      handleLocalSearchChange(
                        'accession_number',
                        e.target.value
                      );
                    }
                  }}
                  placeholder="Number"
                  className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-gray-900 placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-slate-500"
                />
              </div>

              {!selectionOnly && (
                <>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Requesting Physician
                    </label>
                    <Select
                      name="requesting_physician_filter"
                      value={localSearchFilters.requesting_physician}
                      onChange={(e) => {
                        handleLocalSearchChange(
                          'requesting_physician',
                          e.target.value
                        );
                      }}
                      disabled={uniqueRequestingPhysicians.length === 0}
                      className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-900 dark:disabled:text-slate-600"
                    >
                      <option value="">All Physicians</option>
                      {uniqueRequestingPhysicians.map((physician) => (
                        <option key={physician} value={physician}>
                          {physician}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div className="flex min-w-0 flex-col gap-1.5">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Location
                    </label>
                    <Select
                      name="location_filter"
                      value={localSearchFilters.location}
                      onChange={(e) => {
                        handleLocalSearchChange('location', e.target.value);
                      }}
                      disabled={uniqueLocations.length === 0}
                      className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-900 dark:disabled:text-slate-600"
                    >
                      <option value="">All Locations</option>
                      {uniqueLocations.map((location) => (
                        <option key={location} value={location}>
                          {location}
                        </option>
                      ))}
                    </Select>
                  </div>
                </>
              )}
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center justify-end gap-2 lg:mt-[22px]">
              <Button
                onClick={handleSearch}
                disabled={!selectedServer || isQueryingWorklist}
                className="h-9 w-auto px-4"
                size="sm"
              >
                {isQueryingWorklist ? (
                  <span className="flex items-center gap-1.5 text-xs">
                    <FiLoader className="animate-spin" size={14} /> Searching...
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs">
                    <FiSearch size={14} /> Search
                  </span>
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={handleReset}
                disabled={isQueryingWorklist}
                className="h-9 w-auto px-3"
                size="sm"
              >
                <span className="flex items-center gap-1.5 text-xs">
                  <FiRefreshCw size={14} /> Reset
                </span>
              </Button>
              <Button
                variant="secondary"
                onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                className="h-9 w-auto px-3"
                size="sm"
              >
                {isFiltersVisible ? (
                  <span className="flex items-center gap-1.5 text-xs">
                    <FiChevronUp size={14} /> Hide Filters
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs">
                    <FiChevronDown size={14} /> Show Filters
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>

        {isQueryingWorklist ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-sm font-medium text-slate-500">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/20">
                <FiLoader className="animate-spin text-2xl text-blue-600" />
              </span>
              Searching the modality worklist…
            </div>
          </div>
        ) : worklistResults.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-slate-500">
            <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-gray-700">
              <FiSearch className="text-2xl" />
            </span>
            <p className="text-sm font-semibold text-slate-700 dark:text-gray-200">
              {t('noWorklistItemsAdjustFilters')}
            </p>
            <p className="mt-1 max-w-md text-xs leading-relaxed text-slate-400">
              Select a DICOM server, confirm the scheduled date, then search for
              studies available to import.
            </p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto">
              <Table
                table={table}
                isLoading={false}
                columnsLength={table.getAllColumns().length}
                onRowClick={(item) => handleItemSelect(item)}
                isRowSelected={(item) => {
                  if (selectedKeys && selectedKeys.length > 0) {
                    return selectedKeys.includes(mwlItemKey(item));
                  }
                  if (!selectedItem) {
                    return false;
                  }
                  return (
                    item.mwl.PatientID === selectedItem.mwl.PatientID &&
                    item.mwl.AccessionNumber ===
                      selectedItem.mwl.AccessionNumber
                  );
                }}
              />
            </div>

            {/* Resolution Stats at Bottom */}
            {worklistResults.length > 0 && (
              <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800">
                <div className="flex items-center justify-between gap-4 text-sm text-slate-600 dark:text-slate-400">
                  <span>
                    Showing {table.getFilteredRowModel().rows.length} of{' '}
                    {worklistResults.length} result(s)
                  </span>
                  {resolutionStats && (
                    <div className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        {resolutionStats.patients_found} patient(s) in system
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                        {resolutionStats.pregnancies_found} active
                        pregnancy(ies)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WorklistSearch;
